import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Fab,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { toast } from 'react-toastify';
import { adminAPI, paymentAPI } from '../../services/api';
import Layout from '../../components/Layout';

const APIPricing = () => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [creditRate, setCreditRate] = useState('');
  const [gstRate, setGstRate] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  const [savingGST, setSavingGST] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editInputLabel, setEditInputLabel] = useState('');
  const [editInputFields, setEditInputFields] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypePrice, setNewTypePrice] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');
  const [newTypeInputLabel, setNewTypeInputLabel] = useState('');
  const [newInputFields, setNewInputFields] = useState([]);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchPricing();
    fetchCreditRate();
    fetchGSTRate();
  }, []);

  const fetchCreditRate = async () => {
    try {
      const res = await paymentAPI.getCreditRate();
      setCreditRate(res.data.rate.toString());
    } catch (error) {
      // ignore
    }
  };

  const handleSaveCreditRate = async () => {
    const rateValue = parseFloat(creditRate);
    if (!rateValue || rateValue <= 0) {
      toast.error('Rate must be a positive number');
      return;
    }
    setSavingRate(true);
    try {
      await paymentAPI.setCreditRate(rateValue);
      toast.success(`Credit rate updated to ₹${rateValue} per credit`);
    } catch (error) {
      toast.error('Failed to update credit rate');
    } finally {
      setSavingRate(false);
    }
  };

  const fetchGSTRate = async () => {
    try {
      const res = await paymentAPI.getGSTRate();
      setGstRate(res.data.gst.toString());
    } catch (error) {
      // ignore
    }
  };

  const handleSaveGST = async () => {
    const gstValue = parseFloat(gstRate);
    if (isNaN(gstValue) || gstValue < 0) {
      toast.error('GST must be 0 or a positive number');
      return;
    }
    setSavingGST(true);
    try {
      await paymentAPI.setGSTRate(gstValue);
      toast.success(`GST rate updated to ${gstValue}%`);
    } catch (error) {
      toast.error('Failed to update GST rate');
    } finally {
      setSavingGST(false);
    }
  };

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllPricing();
      if (response.success) {
        setPricing(response.data);
      }
    } catch (error) {
      // console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (type) => {
    setSelectedType(type);
    setEditName(type.name);
    setEditPrice(type.price.toString());
    setEditDescription(type.description || '');
    setEditInputLabel(type.input_label || '');
    // Normalize input_fields: ensure each entry is an object, not a string
    const fields = (type.input_fields || []).map(f => 
      typeof f === 'string' 
        ? { name: f, label: '', type: 'text', required: true, placeholder: '', pattern: '', maxLength: '', helperText: '' }
        : f
    );
    setEditInputFields(fields);
    setEditDialog(true);
  };

  const handleDeleteClick = (type) => {
    setSelectedType(type);
    setDeleteDialog(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialog(false);
    setSelectedType(null);
    setEditName('');
    setEditPrice('');
    setEditDescription('');
    setEditInputLabel('');
    setEditInputFields([]);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setNewTypeName('');
    setNewTypePrice('');
    setNewTypeDescription('');
    setNewTypeInputLabel('');
    setNewInputFields([]);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog(false);
    setSelectedType(null);
  };

  // Field management functions for Add dialog
  const addNewField = () => {
    setNewInputFields([...newInputFields, {
      name: '',
      label: '',
      type: 'text',
      required: true,
      placeholder: '',
      pattern: '',
      maxLength: '',
      helperText: ''
    }]);
  };

  const removeNewField = (index) => {
    setNewInputFields(newInputFields.filter((_, i) => i !== index));
  };

  const updateNewField = (index, field, value) => {
    const updated = [...newInputFields];
    updated[index][field] = value;
    // Auto-generate name and placeholder from label
    if (field === 'label') {
      // Convert label to camelCase for field name (e.g., "PAN Number" -> "panNumber")
      const name = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .split(' ')
        .map((word, i) => i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      updated[index].name = name;
      updated[index].placeholder = `Enter ${value}`;
    }
    setNewInputFields(updated);
  };

  // Field management functions for Edit dialog
  const addEditField = () => {
    setEditInputFields([...editInputFields, {
      name: '',
      label: '',
      type: 'text',
      required: true,
      placeholder: '',
      pattern: '',
      maxLength: '',
      helperText: ''
    }]);
  };

  const removeEditField = (index) => {
    setEditInputFields(editInputFields.filter((_, i) => i !== index));
  };

  const updateEditField = (index, field, value) => {
    const updated = [...editInputFields];
    // Guard: normalize string entries to objects
    if (typeof updated[index] === 'string') {
      updated[index] = {
        name: updated[index],
        label: '',
        type: 'text',
        required: true,
        placeholder: '',
        pattern: '',
        maxLength: '',
        helperText: ''
      };
    }
    updated[index][field] = value;
    // Auto-generate name and placeholder from label
    if (field === 'label') {
      // Convert label to camelCase for field name (e.g., "PAN Number" -> "panNumber")
      const name = value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .split(' ')
        .map((word, i) => i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      updated[index].name = name;
      updated[index].placeholder = `Enter ${value}`;
    }
    setEditInputFields(updated);
  };

  const handleUpdatePrice = async () => {
    if (!editName || !editPrice || isNaN(editPrice) || parseFloat(editPrice) < 0) {
      toast.error('Please enter valid name and price');
      return;
    }

    // Validate input fields
    if (editInputFields.length > 0) {
      for (let i = 0; i < editInputFields.length; i++) {
        const field = editInputFields[i];
        if (!field.name || !field.label) {
          toast.error(`Field ${i + 1}: Name and Label are required`);
          return;
        }
      }
    }

    try {
      setUpdating(true);
      const response = await adminAPI.updatePricing(selectedType.id, {
        name: editName,
        price: parseFloat(editPrice),
        description: editDescription,
        input_label: editInputLabel,
        input_fields: editInputFields.length > 0 ? editInputFields : null
      });

      if (response.success) {
        toast.success('Verification type updated successfully');
        handleCloseEditDialog();
        fetchPricing();
      }
    } catch (error) {
      console.error('Error updating verification type:', error);
      toast.error(error.response?.data?.message || 'Failed to update verification type');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName || !newTypePrice || isNaN(newTypePrice) || parseFloat(newTypePrice) < 0) {
      toast.error('Please enter valid name and price');
      return;
    }

    // Validate input fields
    if (newInputFields.length > 0) {
      for (let i = 0; i < newInputFields.length; i++) {
        const field = newInputFields[i];
        if (!field.name || !field.label) {
          toast.error(`Field ${i + 1}: Name and Label are required`);
          return;
        }
      }
    }

    try {
      setUpdating(true);
      const response = await adminAPI.createVerificationType(
        newTypeName,
        parseFloat(newTypePrice),
        newTypeDescription,
        newTypeInputLabel,
        newInputFields.length > 0 ? newInputFields : null
      );

      if (response.success) {
        toast.success('Verification type created successfully');
        handleCloseAddDialog();
        fetchPricing();
      }
    } catch (error) {
      console.error('Error creating verification type:', error);
      toast.error(error.response?.data?.message || 'Failed to create verification type');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteType = async () => {
    try {
      setUpdating(true);
      const response = await adminAPI.deleteVerificationType(selectedType.id);

      if (response.success) {
        toast.success('Verification type deleted successfully');
        handleCloseDeleteDialog();
        fetchPricing();
      }
    } catch (error) {
      console.error('Error deleting verification type:', error);
      toast.error(error.response?.data?.message || 'Failed to delete verification type');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        {/* Credit Rate Setting */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Credit Purchase Rate</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set how much clients pay per credit. This rate applies when clients purchase credits via Razorpay.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="₹ per 1 Credit"
              type="number"
              value={creditRate}
              onChange={(e) => setCreditRate(e.target.value)}
              size="small"
              sx={{ width: 200 }}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <Button 
              variant="contained" 
              onClick={handleSaveCreditRate}
              disabled={savingRate}
            >
              {savingRate ? 'Saving...' : 'Save Rate'}
            </Button>
          </Box>
        </Paper>

        {/* GST Setting */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>GST Percentage</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the GST percentage applied on credit purchases. For example, 18% GST on ₹1/credit = ₹1.18 per credit charged to the client.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="GST %"
              type="number"
              value={gstRate}
              onChange={(e) => setGstRate(e.target.value)}
              size="small"
              sx={{ width: 200 }}
              inputProps={{ min: 0, step: 0.5 }}
            />
            <Button 
              variant="contained" 
              onClick={handleSaveGST}
              disabled={savingGST}
            >
              {savingGST ? 'Saving...' : 'Save GST'}
            </Button>
            {creditRate && gstRate && (
              <Typography variant="body2" color="text.secondary">
                Effective rate: ₹{(parseFloat(creditRate || 0) * (1 + parseFloat(gstRate || 0) / 100)).toFixed(2)} per credit (incl. GST)
              </Typography>
            )}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            API Pricing Management
          </Typography>
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setAddDialog(true)}
            sx={{ boxShadow: 3 }}
            size="medium"
          >
            <AddIcon />
          </Fab>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {pricing.map((type) => (
            <Grid item xs={12} sm={6} md={4} key={type.id}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                    {type.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {type.description || 'No description available'}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      ₹{type.price.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per API call
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                  <IconButton
                    color="primary"
                    onClick={() => handleEditClick(type)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(type)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Add New Type Dialog */}
        <Dialog open={addDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            Add New Verification Type
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Verification Type Name"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                helperText="e.g., MSME, Basic PAN Verification"
                required
              />
              <TextField
                fullWidth
                label="Price Per Call (₹)"
                type="number"
                value={newTypePrice}
                onChange={(e) => setNewTypePrice(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newTypeDescription}
                onChange={(e) => setNewTypeDescription(e.target.value)}
                helperText="Optional description of the verification type"
              />
              
              <Divider sx={{ my: 2 }}>
                <Chip label="Input Fields Configuration" />
              </Divider>

              <Typography variant="body2" color="text.secondary">
                Define the input fields that users will fill when using this verification type.
                Leave empty to use a single default input field.
              </Typography>

              {newInputFields.map((field, index) => (
                <Paper key={index} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Field {index + 1}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => removeNewField(index)}>
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Field Label"
                    value={field.label}
                    onChange={(e) => updateNewField(index, 'label', e.target.value)}
                    placeholder="e.g., PAN Number, Account Number, IFSC Code"
                    required
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.required}
                        onChange={(e) => updateNewField(index, 'required', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Required field"
                  />
                </Paper>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addNewField}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Input Field
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseAddDialog} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={handleAddType}
              variant="contained"
              disabled={updating}
            >
              {updating ? <CircularProgress size={24} /> : 'Add Type'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            Edit Verification Type
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Verification Type Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Price Per Call (₹)"
                type="number"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                helperText="Optional description of the verification type"
              />
              
              <Divider sx={{ my: 2 }}>
                <Chip label="Input Fields Configuration" />
              </Divider>

              <Typography variant="body2" color="text.secondary">
                Define the input fields that users will fill when using this verification type.
                Leave empty to use a single default input field.
              </Typography>

              {editInputFields.map((field, index) => (
                <Paper key={index} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Field {index + 1}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => removeEditField(index)}>
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Field Label"
                    value={field.label}
                    onChange={(e) => updateEditField(index, 'label', e.target.value)}
                    placeholder="e.g., PAN Number, Account Number, IFSC Code"
                    required
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.required}
                        onChange={(e) => updateEditField(index, 'required', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Required field"
                  />
                </Paper>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addEditField}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Input Field
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseEditDialog} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePrice}
              variant="contained"
              disabled={updating}
            >
              {updating ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            Delete Verification Type
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{selectedType?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone. All related data will be affected.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDeleteDialog} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteType}
              variant="contained"
              color="error"
              disabled={updating}
            >
              {updating ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default APIPricing;
